import BugsnagType from '@bugsnag/js'
import 'core-js/features/array/from'
import 'core-js/features/string/ends-with'
import 'core-js/features/string/includes'
import 'core-js/features/string/starts-with'
import 'core-js/stable/promise'
import cuid from 'cuid'
import WebFont from 'webfontloader'

import '../../../common/js/i18n'
import * as headerButtonBar from './components/HeaderButtonBar'
import { getPath } from './components/RegionChooser/hook'
import * as header from './components/header/header'
import * as pricingCalculator from './components/pricing-calculator/pricing-calculator'
import * as regionChooserButton from './components/region-chooser-button/region-chooser-button'
import regionChooser from './components/region-chooser/region-chooser'
import * as searchbar from './components/searchbar/searchbar'
import './sass/index.css'

declare const Bugsnag: typeof BugsnagType

const WebFontConfig = {
  google: {
    families: ['Raleway:300,400,500', 'Roboto:300'],
  },
}

const components = [
  { name: 'header', selector: '.si-header', component: header, lazy: true },
  { name: 'carousel', selector: '.si-component-carousel', lazy: true },
  {
    name: 'pricing-calculator',
    selector: '.si-component-pricing-calculator',
    component: pricingCalculator,
  },
  {
    name: 'graphic-template-list',
    selector: '.si-component-graphic-template-list',
  },
  { name: 'add-to-cart', selector: '.si-component-add-to-cart' },
  {
    name: 'searchbar',
    selector: '.si-component-searchbar',
    component: searchbar,
  },
  { name: 'info-window', selector: '.si-component-info-window' },
  {
    name: 'accordion',
    selector: '.si-component-accordion',
    lazy: true,
  },
  {
    name: 'accordion',
    selector: '.si-component-global-accordion',
    lazy: true,
  },
  {
    name: 'our-materials',
    selector: '.si-component-our-materials',
    lazy: true,
  },
  { name: 'order-history', selector: '.si-component-order-history' },
  { name: 'order-confirmation', selector: '.si-order-confirmation' },
  { name: 'customer-summary', selector: '.si-component-customer-summary' },
  { name: 'myaccount', selector: '.si-component-myaccount' },
  {
    name: 'header-button-bar',
    selector: '.si-component-header-button-bar',
    component: headerButtonBar,
  },
  {
    name: 'region-chooser-button',
    selector: '.si-component-region-chooser-button',
    component: regionChooserButton,
  },
  {
    name: 'register',
    selector: '.si-component-register',
  },
  { name: 'cart', selector: '.si-component-cart' },
  { name: 'activecampaign', selector: 'a[href*="activecampaign-"]' },
  {
    name: 'video',
    selector: '.wistia_embed',
    scripts: ['https://fast.wistia.com/assets/external/E-v1.js'],
  },
  {
    name: 'elfsight',
    selector: '.si-component-elfsight',
    scripts: ['https://apps.elfsight.com/p/platform.js'],
    nullComponent: true,
  },
  {
    name: 'feefo',
    selector: '.si-component-feefo',
    scripts: ['https://api.feefo.com/api/javascript/sticker-it'],
    nullComponent: true,
  },
]

function canUseWebP() {
  var elem = document.createElement('canvas')

  if (!!(elem.getContext && elem.getContext('2d'))) {
    // was able or not to get WebP representation
    return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0
  }

  // very old browser like IE 8, canvas not supported
  return false
}

const flatComponentSelectors = components
  .map((c) => c.selector.replace(/^\./, ''))
  .filter((c) => c.startsWith('si-'))

function importAndAttach(e: Element) {
  const selector = Array.from(e.classList).find((c) =>
    flatComponentSelectors.includes(c)
  )
  if (!selector) {
    return
  }

  const c = components.find((cmp) => cmp.selector === `.${selector}`)

  if (!c) {
    console.warn(`No matching component for .${selector}`)
    return
  }

  if (c.nullComponent) {
    return
  }

  if (c.lazy) {
    console.info(`${c.name} is marked as lazy, skipping (MutationObserver)`)
    return
  }

  ;(c.component
    ? Promise.resolve(c.component)
    : import(`./components/${c.name}/${c.name}`)
  )
    .then((mod) => {
      console.debug(`${c.name} loaded`)

      mod && mod.default && mod.default(e)
    })
    .catch((err) => {
      Bugsnag.notify(err)
    })
}

function watchDOM() {
  new MutationObserver((mutationList, observer) => {
    console.debug('created mutationobserver')
    document.addEventListener('DOMContentLoaded', () => {
      console.debug('document loaded')
      observer.disconnect()
    })

    mutationList.forEach((mr) => {
      Array.from(mr.addedNodes)
        .filter((node) => node.nodeType === 1)
        .forEach((node) => {
          if (!(node instanceof Element)) {
            return
          }

          node.querySelectorAll(`[class*="si-"]`).forEach((cmp) => {
            if (cmp.id !== '') {
              return
            }
            cmp.setAttribute('id', cuid())

            importAndAttach(cmp)
          })
        })
    })
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
  })
}

function main() {
  try {
    const regionPath = getPath()

    if (regionPath && window.location.pathname === '/') {
      if (regionPath !== '/') {
        window.location.href = regionPath
      }
    }
  } catch (_err) {}

  try {
    if (window.location.search.length > 1) {
      const search = window.location.search
      if (search.includes('fbclid')) {
        sessionStorage.setItem('utm_source', 'facebook')
      } else if (search.includes('gclid')) {
        sessionStorage.setItem('utm_source', 'google')
      }
    }
  } catch (_err) {}

  window.addEventListener('load', () => {
    console.debug('body loaded: load')

    try {
      document.body.classList.add(canUseWebP() ? 'webp' : 'no-webp')
    } catch (err) {
      console.error(err)
      Bugsnag.notify(err)
    }

    components.forEach((c) => {
      if (!(c.nullComponent || c.scripts || c.lazy)) {
        return
      }

      const elements: NodeListOf<Element> | any[] = Array.from(
        !c.selector ? [] : document.querySelectorAll(c.selector)
      )

      if (elements.length === 0) {
        return
      }

      const loadComponentPromise = c.nullComponent
        ? Promise.resolve()
        : (c.component
            ? Promise.resolve(c.component)
            : import(`./components/${c.name}/${c.name}`)
          ).then((mod) => {
            console.debug(`${c.name} loaded`)

            elements.forEach((e: Element) => {
              mod && mod.default && mod.default(e)
            })
          })

      return loadComponentPromise
        .then(() => {
          if (!c.scripts) {
            return
          }

          c.scripts.forEach((src) => {
            setTimeout(() => {
              const script: HTMLScriptElement = document.createElement('script')
              script.src = src
              script.async = true
              document.body.appendChild(script)
            }, 10)
          })
        })
        .catch((err) => {
          console.error(
            `An error occurred while loading the ${c.name} component`
          )
          Bugsnag.notify(err)
        })
    })
  })

  WebFont.load(WebFontConfig)

  const regionNode = document.createElement('div')
  regionChooser(regionNode)
}

// function registerServiceWorker() {
//   if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
//     return new Promise((resolve, reject) => {
//       window.addEventListener('load', () => {
//         navigator.serviceWorker
//           .register('/wp-content/themes/stickerit-gb/build/service-worker.js')
//           .then(resolve)
//           .catch(reject)
//       })
//     })
//       .then((registration) => {
//         console.debug('SW registered: ', registration)
//         return true
//       })
//       .catch((registrationError) => {
//         console.debug('SW registration failed: ', registrationError)
//         return false
//       })
//   }

//   return Promise.resolve(false)
// }

// registerServiceWorker().then(() => main())

watchDOM()
main()
